import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import PolarisConfirmModal from "./PolarisConfirmModal";
import {
    POLARIS_HUB_AUDIENCE_ROLES,
    PolarisHubAudienceRole,
    PolarisHubItem,
    PolarisHubItemKind,
    createPolarisHubItem,
    deletePolarisHubItem,
    fetchPolarisHubItems,
    updatePolarisHubItem,
} from "../services/polarisHubContentApi";
import "../styles/polaris-admin.css";

const ROLE_LABELS: Record<PolarisHubAudienceRole, string> = {
    ADMINISTRATOR: "Administrator",
    STAFF: "Staff",
    COACH: "Coach",
    ATHLETE: "Athlete",
    GUARDIAN: "Guardian",
};

const ALL_ROLES = [...POLARIS_HUB_AUDIENCE_ROLES];

type FormState = {
    title: string;
    description: string;
    url: string;
    visibleToRoles: PolarisHubAudienceRole[];
};

const emptyForm = (): FormState => ({
    title: "",
    description: "",
    url: "",
    visibleToRoles: [...ALL_ROLES],
});

function validateName(n: string): string | null {
    const t = n.trim();
    if (t.length < 5 || t.length > 50) {
        return "Title must be between 5 and 50 characters.";
    }
    return null;
}

function hasExplicitScheme(raw: string): boolean {
    const colon = raw.indexOf(":");
    if (colon <= 0) {
        return false;
    }
    return /^[A-Za-z][A-Za-z0-9+.-]*$/.test(raw.slice(0, colon));
}

function normalizeHttpUrl(raw: string): string {
    const trimmed = raw.trim();
    if (!trimmed) {
        return trimmed;
    }
    return hasExplicitScheme(trimmed) ? trimmed : `https://${trimmed}`;
}

function validateUrl(raw: string): string | null {
    const t = raw.trim();
    if (!t) {
        return "URL is required.";
    }
    try {
        const u = new URL(normalizeHttpUrl(t));
        if (u.protocol !== "http:" && u.protocol !== "https:") {
            return "URL must use http or https.";
        }
        if (!u.hostname) {
            return "URL must include a valid host.";
        }
    } catch {
        return "Enter a valid URL.";
    }
    return null;
}

function kindFromPath(pathname: string): PolarisHubItemKind {
    return pathname.endsWith("/resources") ? "RESOURCE" : "VIDEO";
}

export default function PolarisHubContentPage() {
    const location = useLocation();
    const kind = kindFromPath(location.pathname);
    const [items, setItems] = useState<PolarisHubItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState<PolarisHubItem | null>(null);
    const [form, setForm] = useState<FormState>(emptyForm);
    const [formError, setFormError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [pendingDelete, setPendingDelete] = useState<PolarisHubItem | null>(null);
    const [deleting, setDeleting] = useState(false);

    const load = useCallback(async () => {
        setError(null);
        try {
            setItems(await fetchPolarisHubItems(kind));
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Unable to load Hub content.");
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [kind]);

    useEffect(() => {
        setLoading(true);
        load();
    }, [load]);

    const openAdd = useCallback(() => {
        setEditing(null);
        setForm(emptyForm());
        setFormError(null);
        setFormOpen(true);
    }, []);

    const openEdit = useCallback((item: PolarisHubItem) => {
        setEditing(item);
        setForm({
            title: item.title,
            description: item.description ?? "",
            url: item.url,
            visibleToRoles: [...item.visibleToRoles],
        });
        setFormError(null);
        setFormOpen(true);
    }, []);

    const closeForm = useCallback(() => {
        if (saving) {
            return;
        }
        setFormOpen(false);
        setEditing(null);
        setFormError(null);
    }, [saving]);

    const toggleRole = useCallback((role: PolarisHubAudienceRole) => {
        setForm((current) => {
            const next = current.visibleToRoles.includes(role)
                ? current.visibleToRoles.filter((r) => r !== role)
                : [...current.visibleToRoles, role];
            return { ...current, visibleToRoles: next };
        });
    }, []);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        const titleErr = validateName(form.title);
        if (titleErr) {
            setFormError(titleErr);
            return;
        }
        const urlErr = validateUrl(form.url);
        if (urlErr) {
            setFormError(urlErr);
            return;
        }
        if (form.visibleToRoles.length === 0) {
            setFormError("Select at least one role that can see this item.");
            return;
        }
        if (form.description.trim().length > 500) {
            setFormError("Description must be 500 characters or fewer.");
            return;
        }

        setSaving(true);
        setFormError(null);
        try {
            const payload = {
                title: form.title.trim(),
                description: form.description.trim() || null,
                url: normalizeHttpUrl(form.url),
                visibleToRoles: form.visibleToRoles,
            };
            if (editing) {
                await updatePolarisHubItem(editing.id, payload);
            } else {
                await createPolarisHubItem({ ...payload, kind });
            }
            setFormOpen(false);
            setEditing(null);
            await load();
        } catch (e: unknown) {
            setFormError(e instanceof Error ? e.message : "Unable to save this item.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!pendingDelete) {
            return;
        }
        setDeleting(true);
        setError(null);
        try {
            await deletePolarisHubItem(pendingDelete.id);
            setPendingDelete(null);
            await load();
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Unable to delete this item.");
        } finally {
            setDeleting(false);
        }
    };

    const noun = kind === "VIDEO" ? "video" : "resource";
    const heading = useMemo(
        () => (kind === "VIDEO" ? "Hub Videos" : "Hub Resources"),
        [kind]
    );

    return (
        <section className="pa-section" data-testid="polaris-hub-content-page">
            <h1 className="pa-page-title">Hub Content</h1>
            <p className="pa-section-subtitle">
                Publish Polaris videos and resource links that appear on every school Hub. Choose which
                roles can see each item. Schools can still hide these items from their athletes.
            </p>

            <div className="pa-tabs" role="tablist" aria-label="Polaris Hub catalogs">
                <NavLink
                    to="/polaris-admin/hub/videos"
                    role="tab"
                    aria-selected={kind === "VIDEO"}
                    className={({ isActive }) => `pa-tab${isActive ? " active" : ""}`}
                    data-testid="polaris-hub-tab-videos"
                >
                    Videos
                </NavLink>
                <NavLink
                    to="/polaris-admin/hub/resources"
                    role="tab"
                    aria-selected={kind === "RESOURCE"}
                    className={({ isActive }) => `pa-tab${isActive ? " active" : ""}`}
                    data-testid="polaris-hub-tab-resources"
                >
                    Resources
                </NavLink>
            </div>

            <div className="pa-section-header-row">
                <h2 className="pa-section-title" style={{ marginBottom: 0 }}>
                    {heading}
                </h2>
                <button type="button" className="pa-btn pa-btn-primary" onClick={openAdd}>
                    Add {noun}
                </button>
            </div>

            {error && (
                <div className="pa-error" role="alert">
                    {error}
                </div>
            )}
            {loading && <div className="pa-loading">Loading Hub content…</div>}

            {!loading && items.length === 0 && !error && (
                <p className="pa-empty" data-testid="polaris-hub-empty">
                    No {noun}s yet. Add one to share it on every school Hub.
                </p>
            )}

            {!loading && items.length > 0 && (
                <div className="pa-table-wrapper polaris-hub-table-wrap">
                    <table className="pa-table" data-testid="polaris-hub-table">
                        <thead>
                            <tr>
                                <th scope="col">Title</th>
                                <th scope="col">Description</th>
                                <th scope="col">URL</th>
                                <th scope="col">Visible to</th>
                                <th scope="col">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item) => (
                                <tr key={item.id} data-testid={`polaris-hub-row-${item.id}`}>
                                    <td>{item.title}</td>
                                    <td>{item.description || "—"}</td>
                                    <td className="polaris-hub-url-cell">
                                        <a href={item.url} target="_blank" rel="noopener noreferrer">
                                            {item.url}
                                        </a>
                                    </td>
                                    <td>
                                        {item.visibleToRoles
                                            .map((role) => ROLE_LABELS[role] ?? role)
                                            .join(", ")}
                                    </td>
                                    <td>
                                        <div className="polaris-hub-actions">
                                            <button
                                                type="button"
                                                className="pa-btn pa-btn-secondary"
                                                onClick={() => openEdit(item)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                className="pa-btn pa-btn-secondary"
                                                onClick={() => setPendingDelete(item)}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {formOpen && (
                <div
                    className="pa-modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="polaris-hub-form-title"
                    data-testid="polaris-hub-form-overlay"
                >
                    <div className="pa-modal polaris-hub-form-modal">
                        <button
                            type="button"
                            className="pa-modal-close"
                            aria-label="Close"
                            onClick={closeForm}
                            disabled={saving}
                        >
                            ×
                        </button>
                        <h3 id="polaris-hub-form-title" className="pa-modal-title">
                            {editing ? `Edit ${noun}` : `Add ${noun}`}
                        </h3>
                        <form onSubmit={handleSubmit} className="polaris-hub-form">
                            <label className="polaris-hub-field">
                                <span>Title</span>
                                <input
                                    type="text"
                                    value={form.title}
                                    onChange={(e) => setForm((current) => ({ ...current, title: e.target.value }))}
                                    data-testid="polaris-hub-title"
                                    autoComplete="off"
                                />
                            </label>
                            <label className="polaris-hub-field">
                                <span>Description</span>
                                <textarea
                                    value={form.description}
                                    onChange={(e) =>
                                        setForm((current) => ({ ...current, description: e.target.value }))
                                    }
                                    rows={3}
                                    data-testid="polaris-hub-description"
                                />
                            </label>
                            <label className="polaris-hub-field">
                                <span>URL</span>
                                <input
                                    type="text"
                                    inputMode="url"
                                    value={form.url}
                                    onChange={(e) => setForm((current) => ({ ...current, url: e.target.value }))}
                                    data-testid="polaris-hub-url"
                                    autoComplete="off"
                                    placeholder="www.example.com"
                                />
                            </label>
                            <fieldset className="polaris-hub-roles">
                                <legend>Visible to</legend>
                                {ALL_ROLES.map((role) => (
                                    <label key={role} className="pa-checkbox-row">
                                        <input
                                            type="checkbox"
                                            checked={form.visibleToRoles.includes(role)}
                                            onChange={() => toggleRole(role)}
                                            data-testid={`polaris-hub-role-${role}`}
                                        />
                                        {ROLE_LABELS[role]}
                                    </label>
                                ))}
                            </fieldset>
                            {formError && (
                                <p className="pa-modal-error" role="alert">
                                    {formError}
                                </p>
                            )}
                            <div className="pa-modal-actions">
                                <button
                                    type="button"
                                    className="pa-btn pa-btn-secondary"
                                    onClick={closeForm}
                                    disabled={saving}
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="pa-btn pa-btn-primary" disabled={saving}>
                                    {saving ? "Saving…" : editing ? "Save changes" : `Add ${noun}`}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <PolarisConfirmModal
                open={Boolean(pendingDelete)}
                title={`Delete this ${noun}?`}
                message={
                    pendingDelete ? (
                        <>
                            This removes <strong>{pendingDelete.title}</strong> from every school Hub.
                        </>
                    ) : (
                        ""
                    )
                }
                confirmLabel="Delete"
                loadingLabel="Deleting…"
                loading={deleting}
                onConfirm={handleDelete}
                onClose={() => {
                    if (!deleting) {
                        setPendingDelete(null);
                    }
                }}
            />
        </section>
    );
}
